import React from 'react';
import { Instagram, Gift, Calendar, MapPin, Crown, Trophy, Sparkles, Share2, MessageCircle } from 'lucide-react';

// Instagram Square Post (1080x1080) - Full Size Poster
const InstagramSquarePoster = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div 
        className="relative bg-gradient-to-br from-[#0f1219] via-[#141a24] to-[#0a0d12] overflow-hidden"
        style={{ 
          width: '540px', 
          height: '540px',
          fontFamily: "'Inter', sans-serif"
        }}
      >
        {/* Gold Glow Effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[200px] bg-[#FFD700]/15 rounded-full blur-[60px]" />
        
        {/* Confetti Decorations */}
        <div className="absolute top-10 left-10 w-2 h-2 bg-[#FFD700] rotate-45" />
        <div className="absolute top-20 right-16 w-2 h-2 bg-[#8B9A46] rounded-full" />
        <div className="absolute top-32 left-20 w-1.5 h-1.5 bg-[#FFD700]/60 rounded-full" />
        <div className="absolute bottom-24 left-12 w-2 h-2 bg-[#A4B55C] rotate-12" />
        <div className="absolute bottom-32 right-12 w-1.5 h-1.5 bg-[#FFD700] rounded-full" />
        <div className="absolute top-16 right-32 w-1.5 h-1.5 bg-[#FFD700]/40" />
        <div className="absolute bottom-20 left-32 w-2 h-2 bg-[#8B9A46]/60 rotate-45" />
        
        {/* Content Container */}
        <div className="relative z-10 h-full flex flex-col items-center justify-between p-6">
          
          {/* Header - Logo */}
          <div className="flex items-center justify-center w-full">
            <img 
              src="https://www.kingofit.ca/images/logo.png" 
              alt="King of IT"
              className="h-8"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center gap-1">
              <Crown className="w-5 h-5 text-[#FFD700]" />
              <span className="font-bold text-base text-white tracking-wider">KING<span className="text-[#FFD700]">of</span>IT</span>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 flex flex-col items-center justify-center text-center -mt-4">
            {/* Giveaway Badge */}
            <div className="flex items-center gap-1.5 bg-[#FFD700]/20 px-3 py-1 rounded-full border border-[#FFD700]/40 mb-3">
              <Sparkles className="w-3 h-3 text-[#FFD700]" />
              <span className="text-[#FFD700] font-semibold text-[10px] tracking-wide">EXCLUSIVE GIVEAWAY</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="font-black text-4xl text-white leading-none tracking-tight mb-0.5" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              YOU COULD
            </h1>
            <h1 className="font-black text-5xl leading-none tracking-tight mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", background: 'linear-gradient(135deg, #FFD700, #FFA500)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              WIN BIG!
            </h1>
            
            {/* Prize Card */}
            <div className="relative bg-gradient-to-br from-[#1a2030] to-[#0f141c] rounded-2xl p-4 border border-[#FFD700]/20 shadow-xl mb-4">
              {/* Trophy Icon */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-full flex items-center justify-center shadow-lg">
                <Trophy className="w-4 h-4 text-[#0f1219]" />
              </div>
              
              <div className="pt-3">
                <p className="text-[#8B9A46] text-[8px] font-medium mb-0.5">GRAND PRIZE</p>
                <p className="text-4xl font-black text-[#FFD700] mb-0.5" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>$100</p>
                <p className="text-white text-base font-light" style={{ fontFamily: "'Playfair Display', serif" }}>Gift Card</p>
                
                <div className="mt-2 pt-2 border-t border-white/10">
                  <p className="text-gray-400 text-[7px] mb-1">Redeemable at</p>
                  <div className="flex items-center justify-center gap-1.5">
                    <div className="w-4 h-4 bg-[#8B9A46]/30 rounded-full flex items-center justify-center">
                      <Gift className="w-2 h-2 text-[#A4B55C]" />
                    </div>
                    <span className="text-white text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>Olive Branch</span>
                  </div>
                  <div className="flex items-center justify-center gap-0.5 mt-0.5 text-gray-500 text-[7px]">
                    <MapPin className="w-2 h-2" />
                    <span>Promenade Mall, Thornhill</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Draw Date */}
            <div className="flex items-center gap-1.5 text-white mb-3">
              <Calendar className="w-3 h-3 text-[#FFD700]" />
              <span className="text-xs">Draw: <span className="text-[#FFD700] font-semibold">Sunday, January 25, 2026</span></span>
            </div>
          </div>
          
          {/* Footer - How to Enter */}
          <div className="w-full">
            <p className="text-gray-400 text-center text-[8px] mb-2">HOW TO ENTER</p>
            <div className="flex items-center justify-center gap-3">
              <div className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-full border border-white/10">
                <Instagram className="w-2.5 h-2.5 text-[#FFD700]" />
                <span className="text-white text-[9px]">Follow</span>
              </div>
              <div className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-full border border-white/10">
                <Share2 className="w-2.5 h-2.5 text-[#FFD700]" />
                <span className="text-white text-[9px]">Share</span>
              </div>
              <div className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-full border border-white/10">
                <MessageCircle className="w-2.5 h-2.5 text-[#FFD700]" />
                <span className="text-white text-[9px]">DM Us</span>
              </div>
            </div>
            <p className="text-center mt-2">
              <span className="text-[#FFD700] font-semibold text-sm">@kingofit.ca</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstagramSquarePoster;
